"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PageLayout = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const Box_1 = tslib_1.__importDefault(require("@mui/material/Box"));
const shallowEqual_1 = require("../_InternalUtils/shallowEqual");
const InternalComponents_1 = require("./InternalComponents");
const InternalComponents_2 = require("./InternalComponents");
const PageHeader_1 = tslib_1.__importDefault(require("./PageHeader"));
const ServiceNavigation_1 = tslib_1.__importDefault(require("./ServiceNavigation"));
const SidebarLeft_1 = tslib_1.__importDefault(require("./SidebarLeft"));
const SidebarRight_1 = tslib_1.__importDefault(require("./SidebarRight"));
/**
 *
 * A common component that defines the primary layout for all pages in Argus.
 *
 * @remarks
 * The layout component has 6 different sections. The initial documentation of the different sections is located at https://wiki.mnemonic.no/display/DEV/Argus+page+layout.
 * 1. The Argus menu. It is not a part of this component, but this component is directly coupled to the height of the global menu
 * 2. Service navigation. A rail on the left side of the screen intended for navigation within a argus service
 * 3. The page header. The header which is specific for each page. Each page will define the content of the header
 * 4. Main content. The main content of the page.
 * 5. Left sidebar. The left sidebar is intended to be used for internal page navigation or selecting from a set of options which determine the content of the main section
 * 5. Right sidebar. The right sidebar should display more detailed information about the main subject of the page. The main subject can be either the specific entity (for view pages) or the selected item in a list
 *
 *
 * @example Usage only with the page header
 * ```tsx
 * <PageLayout
 *   ServiceNavigationProps={{ isEnabled: false }}
 *   SidebarLeftProps={{ isEnabled: false }}
 *   SidebarRightProps={{ isEnabled: false }}
 * >
 *   This will appear as the main content
 * </PageLayout>
 * ```
 *
 * @example Usage with SidebarRight enabled
 * ```tsx
 * <PageLayout
 *   ServiceNavigationProps={{ isEnabled: false }}
 *   SidebarLeftProps={{ isEnabled: false }}
 *   SidebarRightProps={{
 *     isEnabled: true
 *     open: true,
 *     title: "Right sidebar",
 *     onSelectedValueChange: (e, newSelectedValue) => setSelectedRightSidebarTab(newSelectedValue),
 *     onOpen: () => setIsRightSidebarOpen(true),
 *     onClose: () => setIsRightSidebarOpen(false),
 *     selectedValue: 0,
 *     tabs: [
 *      { value: 0,  content: <span>This is tab 0</span> },
 *      { value: 1, content: <span>This is tab 1</span> },
 *     ],
 *   }}
 * >
 *    This will appear as the main content
 * </PageLayout>
 *
 * ```
 */
const PageLayout = (props) => {
    const { ServiceNavigationProps: serviceNavigationProps, SidebarRightProps: sidebarRightPropsProp, SidebarLeftProps: sidebarLeftPropsProp, PageHeaderProps: pageHeaderPropsProp, children, } = props;
    const { isEnabled: isServiceNavigationEnabled, ...restOfServiceNavigationProps } = serviceNavigationProps;
    const { isEnabled: isSidebarRightEnabled, ...restOfSidebarRightProps } = sidebarRightPropsProp;
    const { isEnabled: isSidebarLeftEnabled, ...restOfSidebarLeftProps } = sidebarLeftPropsProp;
    const styles = getStyles({ props: { isEnabled: serviceNavigationProps?.isEnabled } });
    return (react_1.default.createElement(InternalComponents_1.PageLayoutWrapper, { className: "MnePageLayoutWrapper" },
        isServiceNavigationEnabled && (react_1.default.createElement(ServiceNavigation_1.default, { ...restOfServiceNavigationProps })),
        react_1.default.createElement(Box_1.default, { sx: styles.flexGrow },
            react_1.default.createElement(PageHeader_1.default, { ...pageHeaderPropsProp }),
            react_1.default.createElement(Box_1.default, { sx: styles.contentWrapper },
                isSidebarLeftEnabled && react_1.default.createElement(SidebarLeft_1.default, { ...restOfSidebarLeftProps }),
                react_1.default.createElement(Box_1.default, { sx: styles.contentInnerContainer },
                    react_1.default.createElement(InternalComponents_2.WrapperMainContent, null,
                        react_1.default.createElement(InternalComponents_1.InnerContainerMainContent, { className: "MneInnerContainerMainContent" }, children))),
                isSidebarRightEnabled && react_1.default.createElement(SidebarRight_1.default, { ...restOfSidebarRightProps })))));
};
exports.PageLayout = PageLayout;
const getStyles = ({ props }) => ({
    root: props?.isEnabled ? { display: "flex", flexDirection: "row" } : { display: "block" },
    contentWrapper: {
        display: "flex",
    },
    contentInnerContainer: {
        flex: "1 1",
    },
    flexGrow: {
        flexGrow: 1,
    },
});
exports.default = react_1.default.memo(exports.PageLayout, (prevProps, nextProps) => {
    const { ServiceNavigationProps: prevServiceNavigationProps, PageHeaderProps: prevPageHeaderProps, SidebarRightProps: prevSidebarRightProps, SidebarLeftProps: prevSidebarLeftProps, ...restOfPrevProps } = prevProps;
    const { ServiceNavigationProps: nextServiceNavigationProps, PageHeaderProps: nextPageHeaderProps, SidebarRightProps: nextSidebarRightProps, SidebarLeftProps: nextSidebarLeftProps, ...restOfNextProps } = nextProps;
    return ((0, shallowEqual_1.shallowEqual)(prevServiceNavigationProps, nextServiceNavigationProps) &&
        (0, shallowEqual_1.shallowEqual)(prevPageHeaderProps, nextPageHeaderProps) &&
        (0, shallowEqual_1.shallowEqual)(prevSidebarRightProps, nextSidebarRightProps) &&
        (0, shallowEqual_1.shallowEqual)(prevSidebarLeftProps, nextSidebarLeftProps) &&
        (0, shallowEqual_1.shallowEqual)(restOfPrevProps, restOfNextProps));
});
